import { ArrowLeft, Upload, FileText, ExternalLink } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../../lib/api';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
    });
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getStatusBadge = (status) => {
    const statusMap = {
        'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
        'accepted': { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
        'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700' },
    };
    return statusMap[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-700' };
};

// Helper function to get file type badge
const getFileTypeBadge = (fileType) => {
    if (!fileType) return { label: 'File', className: 'bg-gray-100 text-gray-700' };
    if (fileType.startsWith('image/')) {
        return { label: 'Image', className: 'bg-blue-100 text-blue-700' };
    }
    if (fileType.includes('pdf')) {
        return { label: 'PDF', className: 'bg-red-100 text-red-700' };
    }
    return { label: 'File', className: 'bg-gray-100 text-gray-700' };
};

export default function QuotesDetails() {
    const navigate = useNavigate();
    const { id } = useParams();

    const { data: quote, isLoading, error, refetch } = useQuery({
        queryKey: ['quoteDetails', id],
        queryFn: async () => {
            const response = await api.get(`/company/quotes/${id}`);
            return response.data.data;
        },
        enabled: !!id,
    });

    if (isLoading) {
        return (
            <div className="min-h-screen p-4 text-sm">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-600">Loading quote...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen p-4 text-sm">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-red-600 mb-4">Failed to load quote</p>
                    <button
                        onClick={() => refetch()}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition text-xs"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!quote) {
        return (
            <div className="min-h-screen p-4 text-sm">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-600">Quote not found</p>
                </div>
            </div>
        );
    }

    const statusBadge = getStatusBadge(quote.status);
    const clientName = `${quote.first_name || ''} ${quote.last_name || ''}`.trim() || quote.email || 'Unknown';

    return (
        <div className="min-h-screen p-4 text-sm">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 p-4 bg-gradient-to-b from-green-50/20 to-white">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                                        {statusBadge.label}
                                    </span>
                                    <h1 className="text-xl font-bold text-gray-900">Quote #{quote.id}</h1>
                                </div>
                                <p className="text-xs text-gray-600 mt-0.5">{clientName}</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mt-4 text-xs">
                        <div>
                            <h3 className="font-medium text-gray-900 mb-1.5">Boat Details</h3>
                            <div className="text-gray-700 space-y-0.5">
                                {quote.boat_type && quote.boat_make && quote.boat_year && (
                                    <p>{quote.boat_type} • {quote.boat_make} • {quote.boat_year}</p>
                                )}
                                {quote.boat_length && (
                                    <p>Length: {quote.boat_length}</p>
                                )}
                                {quote.boat_location && (
                                    <p>Location: {quote.boat_location}</p>
                                )}
                                {quote.zip_code && (
                                    <p>ZIP: {quote.zip_code}</p>
                                )}
                                {!quote.boat_type && !quote.boat_make && !quote.boat_year && !quote.boat_length && !quote.boat_location && (
                                    <p className="text-gray-500">No boat details available</p>
                                )}
                            </div>
                        </div>
                        <div>
                            <h3 className="font-medium text-gray-900 mb-1.5">Contact</h3>
                            <div className="text-gray-700 space-y-0.5">
                                {quote.phone && <p>{quote.phone}</p>}
                                {quote.email && <p>{quote.email}</p>}
                                {quote.insurance_company_email && (
                                    <p className="text-gray-600">Insurance: {quote.insurance_company_email}</p>
                                )}
                                {!quote.phone && !quote.email && (
                                    <p className="text-gray-500">No contact information</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 text-xs">
                        <div>
                            <span className="text-gray-500">Service</span>
                            <div className="font-medium mt-0.5">{quote.service?.name || 'N/A'}</div>
                        </div>
                        <div>
                            <span className="text-gray-500">Lead Source</span>
                            <div className="font-medium mt-0.5">
                                {quote.lead_source?.name ? (
                                    quote.lead_source.name
                                ) : (
                                    <a
                                        href="https://oceanmarinesurveyors.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1"
                                    >
                                        oceanmarinesurveyors.com
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                            </div>
                        </div>
                        <div>
                            <span className="text-gray-500">Amount</span>
                            <div className="font-medium mt-0.5 text-emerald-600">
                                {quote.amount ? `$${parseFloat(quote.amount).toFixed(2)}` : 'N/A'}
                            </div>
                        </div>
                        <div>
                            <span className="text-gray-500">Created</span>
                            <div className="font-medium mt-0.5">{formatDate(quote.created_at)}</div>
                        </div>
                        <div>
                            <span className="text-gray-500">Status</span>
                            <div className="mt-0.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
                                    {statusBadge.label}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-b border-gray-200 bg-gradient-to-b from-white to-gray-50/20">
                    <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-1.5">
                        <span className="w-1 h-6 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></span>
                        Survey & Boat Summary
                    </h2>

                    <div className="space-y-3">
                        <div className="border border-gray-200 rounded-lg p-3 bg-white hover:border-green-300 transition text-xs">
                            <div className="space-y-2">
                                <div>
                                    <h4 className="font-medium text-gray-900">{quote.service?.name || 'Service'}</h4>
                                    {quote.service?.description && (
                                        <p className="text-gray-600 text-xs mt-0.5">{quote.service.description}</p>
                                    )}
                                </div>
                                <div className="pt-2 border-t border-gray-100">
                                    <p className="text-xs text-gray-500 mb-1">Boat Information:</p>
                                    <div className="text-gray-700 space-y-0.5">
                                        {quote.boat_type && (
                                            <p>Type: {quote.boat_type}</p>
                                        )}
                                        {quote.boat_make && (
                                            <p>Make: {quote.boat_make}</p>
                                        )}
                                        {quote.boat_year && (
                                            <p>Year: {quote.boat_year}</p>
                                        )}
                                        {quote.boat_length && (
                                            <p>Length: {quote.boat_length}</p>
                                        )}
                                        {quote.boat_location && (
                                            <p>Location: {quote.boat_location}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-base font-medium text-gray-900 mb-1.5">Client Message</h2>
                    {quote.message ? (
                        <p className="text-gray-700 text-xs whitespace-pre-wrap">{quote.message}</p>
                    ) : (
                        <p className="text-gray-500 text-xs italic">No message from client.</p>
                    )}
                </div>

                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-base font-medium text-gray-900 mb-1.5">Disclaimer</h2>
                    <p className="text-xs text-gray-700">
                        Valid for 30 days. Prices may change after.
                    </p>
                </div>

                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-base font-medium text-gray-900 mb-1.5">Internal Notes</h2>
                    <p className="text-xs text-gray-600 italic mb-2">
                        Team only
                    </p>
                    <textarea
                        placeholder="Add note..."
                        className="w-full p-2.5 border border-gray-300 rounded-lg resize-none focus:ring-1 focus:ring-green-500 text-xs"
                        rows="2"
                    />
                </div>

                <div className="p-4">
                    <h2 className="text-base font-medium text-gray-900 mb-3">Attached Files</h2>

                    {quote.files && quote.files.length > 0 ? (
                        <div className="space-y-3">
                            {quote.files.map((file) => {
                                const fileTypeBadge = getFileTypeBadge(file.file_type);
                                return (
                                    <div key={file.id} className="bg-white border border-gray-200 rounded-lg p-3 text-xs hover:border-green-300 transition">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FileText className="w-4 h-4 text-gray-500" />
                                                    <span className="font-medium text-gray-900">{file.file_name}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${fileTypeBadge.className}`}>
                                                        {fileTypeBadge.label}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    {formatFileSize(file.file_size)} • {formatDate(file.created_at)}
                                                </p>
                                            </div>
                                            <a
                                                href={file.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 hover:bg-emerald-700 transition"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                                View
                                            </a>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="border border-dashed border-gray-300 rounded-lg p-5 text-center">
                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-1.5" />
                            <p className="text-xs text-gray-600">
                                No files attached
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}